import {
  type DateSelectorPresetId,
  DATE_SELECTOR_PRESET_ORDER,
  isCustomDateSelectorPreset,
} from '@app/components/date-range-selector/date-selector-presets';
import { createParser } from 'nuqs';

const VALID_PRESET_IDS = new Set<string>(DATE_SELECTOR_PRESET_ORDER);

/**
 * URL-serializable value for a single-date filter.
 * For preset-based dates only presetId is needed; for custom selection, date is an ISO string.
 */
export interface SingleDateFilterValue {
  presetId: DateSelectorPresetId;
  date?: string;
}

/**
 * nuqs parser for single-date filter values.
 * Non-custom presets: plain string in URL (e.g. "today").
 * Custom selection: JSON with presetId and date.
 */
export const parseAsSingleDateFilter = createParser<SingleDateFilterValue | null>({
  parse(value) {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();

    if (VALID_PRESET_IDS.has(trimmed)) {
      if (isCustomDateSelectorPreset(trimmed as DateSelectorPresetId)) {
        return null;
      }

      return { presetId: trimmed as DateSelectorPresetId };
    }

    try {
      const raw = JSON.parse(value) as unknown;
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const rawObj = raw as Record<string, unknown>;
      if (!('presetId' in rawObj)) {
        return null;
      }

      const presetId = String(rawObj.presetId);
      if (!VALID_PRESET_IDS.has(presetId)) {
        return null;
      }

      const date = typeof rawObj.date === 'string' ? rawObj.date : undefined;
      if (isCustomDateSelectorPreset(presetId as DateSelectorPresetId) && !date) {
        return null;
      }

      return { presetId: presetId as DateSelectorPresetId, date };
    } catch {
      return null;
    }
  },
  serialize(value): string {
    if (value == null) {
      return '';
    }

    if (!isCustomDateSelectorPreset(value.presetId)) {
      return value.presetId;
    }

    return JSON.stringify({
      presetId: value.presetId,
      ...(value.date != null && { date: value.date }),
    });
  },
  eq(a, b) {
    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return false;
    }

    return a.presetId === b.presetId && (a.date ?? '') === (b.date ?? '');
  },
});
